import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Inbox, ArrowRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PremiumBadge } from "@/components/Badges";

const STATUS = {
  sent: { label: "Nouvelle", color: "bg-[#EEF3FF] text-[#3B5BDB] border-[#BFDBFE]" },
  viewed: { label: "Vue", color: "bg-[#FAF3EA] text-[#666] border-[#E5E5E5]" },
  shortlisted: { label: "Shortlistée", color: "bg-[#FAF3EA] text-[#8B5A2B] border-[#E6C29A]" },
  accepted: { label: "Acceptée", color: "bg-[#E7F5EC] text-[#2E7D32] border-[#BFE4C5]" },
  refused: { label: "Refusée", color: "bg-red-50 text-[#D32F2F] border-red-200" },
};

export default function ProApplications() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!loading && user && (user.role !== "creator" && user.role !== "professional")) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if ((user?.role === "creator" || user?.role === "professional")) {
      api.get("/pro/applications")
        .then(({ data }) => setApps(Array.isArray(data) ? data : []))
        .catch(() => setApps([]));
    }
  }, [user]);

  const filtered = apps.filter((a) => {
    if (filter !== "all" && a.status !== filter) return false;
    if (search && !(a.talent?.name || "").toLowerCase().includes(search.toLowerCase()) && !(a.collaboration?.title || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <AppShell>
      <div data-testid="pro-applications-page">
        <div className="mb-8">
          <p className="caption-label text-[#C68E59]">Candidatures reçues</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1">Toutes les candidatures</h1>
          <p className="text-[#666] mt-2 text-sm">{apps.length} candidature{apps.length > 1 ? "s" : ""} reçue{apps.length > 1 ? "s" : ""} au total.</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B8680]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un talent ou collab…" className="rounded-xl pl-9" data-testid="app-search" />
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { v: "all", l: "Toutes" },
              { v: "sent", l: "Nouvelles" },
              { v: "viewed", l: "Vues" },
              { v: "shortlisted", l: "Shortlist" },
              { v: "accepted", l: "Acceptées" },
              { v: "refused", l: "Refusées" },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition ${
                  filter === f.v
                    ? "bg-[#C68E59] border-[#C68E59] text-white"
                    : "bg-white border-[#D4C3A5] text-[#666] hover:border-[#1A1A1A]"
                }`}
                data-testid={`filter-${f.v}`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#EADDC9] p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#F5E6D0] flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-6 h-6 text-[#C68E59]" />
            </div>
            <p className="font-serif text-xl text-[#1A1A1A] mb-2">Aucune candidature</p>
            <p className="text-sm text-[#8B8680] mb-6">
              {apps.length === 0
                ? "Publiez une collaboration pour commencer à recevoir des candidatures."
                : "Aucune candidature ne correspond à votre recherche."}
            </p>
            {apps.length === 0 && (
              <Link to="/createurs/collaborations?new=1">
                <button className="inline-flex items-center gap-2 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white text-xs font-semibold px-5 py-2.5 transition">
                  Publier une collaboration <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </Link>
            )}
          </div>
        )}

        <div className="space-y-3">
          {filtered.map((a) => {
            const s = STATUS[a.status] || STATUS.sent;
            return (
              <Link
                key={a.id}
                to={`/pro/collaborations/${a.collaboration_id}/applications`}
                className="bg-white rounded-2xl border border-[#EADDC9] p-5 flex items-center gap-4 hover:shadow-[0_4px_14px_rgba(0,0,0,0.04)] transition"
                data-testid={`pro-app-row-${a.id}`}
              >
                <TalentAvatar userId={a.talent_user_id} name={a.talent?.name} demo={a.talent_profile?.demo_photo_url} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-serif text-lg text-[#1A1A1A] leading-tight">{a.talent?.name}</p>
                    {a.talent_profile?.is_premium && <PremiumBadge />}
                  </div>
                  <p className="text-sm text-[#8B8680] truncate mt-0.5">
                    Pour « {a.collaboration?.title} »
                  </p>
                  <p className="text-xs text-[#999] mt-1">
                    {new Date(a.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${s.color}`}>
                  {s.label}
                </span>
                <ArrowRight className="w-4 h-4 text-[#8B8680] flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}

function TalentAvatar({ userId, name, demo }) {
  const [url, setUrl] = useState(demo || null);
  useEffect(() => {
    if (url) return;
    api.get(`/portfolio/${userId}`)
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : [];
        const main = arr.find((p) => p.is_main) || arr[0];
        if (main) setUrl(`${API}/img/${main.storage_path}?w=1600&q=86`);
      })
      .catch(() => {});
  }, [userId]);
  return (
    <div className="w-12 h-12 rounded-full bg-[#F5E6D0] overflow-hidden flex-shrink-0 flex items-center justify-center">
      {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : <span className="text-sm font-semibold text-[#1A1A1A]">{name?.[0] || "T"}</span>}
    </div>
  );
}
